import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, Share2, ChevronLeft, ChevronRight, Volume2, VolumeX, MessageCircle, ZoomIn, X, Check } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import Spinner from '../components/Spinner';
import Image from '../components/Image';
import DynamicSectionsRenderer from '../components/DynamicSectionsRenderer';
import ProductReviews from '../components/ProductReviews';
import FloatingMiniVideoPlayer from '../components/FloatingMiniVideoPlayer';
import { getProduct, getProducts, getCategories, getProductReviewStats, resolveStorefrontImageUrl, LuxWebCache } from '../api/client';
import { getTranslation as t, isRTL } from '../utils/translations';
import Hls from 'hls.js';
import Web3DViewer from '../components/Web3DViewer';
import RealProduct360Viewer from '../components/RealProduct360Viewer';
import { RotateCw, Box as BoxIcon, Sparkles as SparklesIcon } from 'lucide-react';

const RECENTLY_VIEWED_KEY = 'luxvono_recently_viewed_products';

const sortCategories = (items = []) => {
    return [...items].sort((a, b) => {
        const aOrder = Number(a?.sortOrder ?? 9999);
        const bOrder = Number(b?.sortOrder ?? 9999);
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
};

const ProductPage = ({ onAddToCart, onBuyNow, storeSettings }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [product, setProduct] = useState(() => LuxWebCache.findCachedProduct(id));
    const [allProducts, setAllProducts] = useState(() => LuxWebCache.getCachedProductList());
    const [categories, setCategories] = useState(() => sortCategories(LuxWebCache.getCachedCategoryList()));
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(() => !LuxWebCache.findCachedProduct(id));
    const [mediaList, setMediaList] = useState([]);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isMuted, setIsMuted] = useState(true);
    const [activeTab, setActiveTab] = useState('gallery');
    const [isDraggingThumb, setIsDraggingThumb] = useState(false);
    const [thumbStartX, setThumbStartX] = useState(0);
    const [thumbScrollLeft, setThumbScrollLeft] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isSubmittingCart, setIsSubmittingCart] = useState(false);
    const [isSubmittingBuy, setIsSubmittingBuy] = useState(false);
    const [cartAddedToast, setCartAddedToast] = useState(false);
    const [shareCopiedToast, setShareCopiedToast] = useState(false);
    const [recentlyViewedList, setRecentlyViewedList] = useState([]);
    const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const touchStartRef = useRef({ x: 0, y: 0 });
    const touchEndRef = useRef({ x: 0, y: 0 });
    const thumbStripRef = useRef(null);

    const getWholesaleTiers = useCallback((prod) => {
        if (!prod || !prod.attributes) return [];
        const attr = prod.attributes.find(a => a.name === 'WHOLESALE_TIERS');
        if (!attr) return [];
        try {
            const valStr = attr.detailedValues?.[0]?.value || attr.values;
            if (!valStr) return [];
            const parsed = JSON.parse(valStr);
            return Array.isArray(parsed) ? parsed.filter(t => t.status === 'Enabled') : [];
        } catch (e) { return []; }
    }, []);

    const getInventoryRules = useCallback((prod) => {
        if (!prod || !prod.attributes) return { trackInventory: true, availabilityLabel: 'In Stock' };
        const attr = prod.attributes.find(a => a.name === 'INVENTORY_RULES');
        if (!attr) return { trackInventory: true, availabilityLabel: 'In Stock' };
        try {
            const valStr = attr.detailedValues?.[0]?.value || attr.values;
            if (!valStr) return { trackInventory: true, availabilityLabel: 'In Stock' };
            return { trackInventory: true, availabilityLabel: 'In Stock', ...JSON.parse(valStr) };
        } catch (e) { return { trackInventory: true, availabilityLabel: 'In Stock' }; }
    }, []);

    const resolveImageUrl = useCallback((url) => {
        if (!url) return '/placeholder.png';
        if (/^https?:\/\//i.test(url)) return url;
        if (url.startsWith('/media/')) return url;
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'https://api.luxvono.com').replace(/\/$/, '');
        return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
    }, []);

    const goToMedia = useCallback((index) => {
        setActiveMediaIndex(current => {
            if (!mediaList.length) return 0;
            const normalized = ((index % mediaList.length) + mediaList.length) % mediaList.length;
            return normalized === current ? current : normalized;
        });
    }, [mediaList.length]);

    const goNext = useCallback(() => {
        setActiveMediaIndex(current => (mediaList.length ? (current + 1) % mediaList.length : 0));
    }, [mediaList.length]);

    const goPrev = useCallback(() => {
        setActiveMediaIndex(current => (mediaList.length ? (current - 1 + mediaList.length) % mediaList.length : 0));
    }, [mediaList.length]);

    const handleTouchStart = useCallback((event) => {
        const touch = event.changedTouches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchEndRef.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    const handleTouchMove = useCallback((event) => {
        const touch = event.changedTouches[0];
        touchEndRef.current = { x: touch.clientX, y: touch.clientY };
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (mediaList.length <= 1) return;
        const deltaX = touchEndRef.current.x - touchStartRef.current.x;
        const deltaY = touchEndRef.current.y - touchStartRef.current.y;
        const isHorizontalSwipe = Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY);
        if (!isHorizontalSwipe) return;
        if (deltaX < 0) goNext();
        else goPrev();
    }, [goNext, goPrev, mediaList.length]);

    useEffect(() => {
        window.scrollTo(0, 0);
        let isCancelled = false;

        const applyProductMediaAndAttrs = (p, isInitialSeed = false) => {
            if (!p || isCancelled) return;
            setProduct(p);
            setLoading(false);

            const imageGallery = Array.isArray(p?.images) && p.images.length > 0
                ? p.images.filter(Boolean)
                : [p?.primaryImageUrl || p?.image].filter(Boolean);

            const pPageVideos = Array.isArray(p?.videos)
                ? p.videos.filter(v => v.showInProductPage !== false).map(v => v.videoUrl).filter(Boolean)
                : (p?.videoUrl ? [p.videoUrl] : []);

            const allVideos = [
                ...(p?.typeMetadata?.catwalkVideoUrl ? [p.typeMetadata.catwalkVideoUrl] : []),
                ...pPageVideos
            ].filter(Boolean);

            // Primary product images come first so the hero gallery always opens on crisp product photography,
            // while associated videos appear both in the thumbnail strip and in the FloatingMiniVideoPlayer.
            const finalMedia = [...new Set([...imageGallery, ...allVideos])];
            setMediaList(finalMedia.length ? finalMedia : ['/placeholder.png']);
            if (isInitialSeed) {
                setActiveMediaIndex(0);
            }
            LuxWebCache.preloadImages(imageGallery);

            // Pre-select attributes that have only one option
            const singleOptions = {};
            if (p?.attributes) {
                p.attributes.filter(attr => !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name)).forEach(attr => {
                    const options = Array.isArray(attr.detailedValues) 
                        ? attr.detailedValues 
                        : (typeof attr.values === 'string' 
                            ? attr.values.split(',').map(v => ({ value: v.trim() })) 
                            : []);
                    if (options.length === 1) {
                        const valObj = options[0];
                        singleOptions[attr.name] = (valObj && typeof valObj === 'object') ? (valObj.value || valObj) : valObj;
                    }
                });
            }
            setSelectedAttributes(singleOptions);

            const tiers = getWholesaleTiers(p);
            if (tiers.length > 0 && Number(tiers[0].minQty) > 1) {
                setQuantity(Number(tiers[0].minQty));
            } else {
                setQuantity(1);
            }
        };

        // 0ms Instant Seed from LuxWebCache (Alibaba / Instagram Progressive Architecture)
        const cachedSeed = LuxWebCache.findCachedProduct(id);
        if (cachedSeed) {
            applyProductMediaAndAttrs(cachedSeed, true);
        } else {
            setLoading(true);
        }

        const primaryPromise = getProduct(id)
            .then(p => {
                if (p) {
                    applyProductMediaAndAttrs(p, !cachedSeed);
                    try {
                        import('../api/client').then(m => {
                            if (m.trackCustomerEvent) {
                                m.trackCustomerEvent('PRODUCT_VIEW', {
                                    productId: p.id,
                                    categoryId: p.categoryId || p.category?.id || null,
                                    metadata: { productName: p.name || p.nameAr || '' }
                                });
                            }
                        }).catch(() => {});
                    } catch (_) {}
                }
                return p;
            })
            .catch(() => cachedSeed)
            .finally(() => {
                if (!isCancelled) setLoading(false);
            });

        // Hydrate recommendations, categories, and review stats in the background without blocking Product display
        Promise.all([
            primaryPromise,
            getProducts(0, 60),
            getCategories(),
            getProductReviewStats(id).catch(() => ({ averageRating: 0, totalReviews: 0 }))
        ]).then(([p, allProductsRes, allCategoriesRes, statsRes]) => {
            if (isCancelled || !p) return;
            if (statsRes) {
                setReviewStats(statsRes);
            }
            const allProductsList = Array.isArray(allProductsRes?.data)
                ? allProductsRes.data
                : (Array.isArray(allProductsRes) ? allProductsRes : []);
            setAllProducts(allProductsList);
            const sortedCategories = sortCategories(Array.isArray(allCategoriesRes) ? allCategoriesRes : []);
            setCategories(sortedCategories);
            const currentCategoryId = Number(p?.categoryId || p?.category?.id || 0);
            const currentCategoryName = String(p?.categoryName || p?.category || '').toLowerCase();
            const recommendationMode = String(storeSettings?.recommendationMode || 'same-category-first').toLowerCase();
            const minSameCategory = Math.min(12, Math.max(1, Number(storeSettings?.recommendationMinSameCategory) || 4));
            const nearbyWindow = Math.min(6, Math.max(1, Number(storeSettings?.recommendationNearbyCategoryWindow) || 2));

            const sameCategory = allProductsList.filter(item => {
                if (Number(item?.id) === Number(p?.id)) return false;
                const itemCategoryId = Number(item?.categoryId || item?.category?.id || 0);
                const itemCategoryName = String(item?.categoryName || item?.category || '').toLowerCase();
                if (currentCategoryId > 0 && itemCategoryId > 0) {
                    return itemCategoryId === currentCategoryId;
                }
                return itemCategoryName && itemCategoryName === currentCategoryName;
            });

            const selected = [];
            const selectedIds = new Set();
            const pushIfNew = (item) => {
                const productId = Number(item?.id);
                if (!Number.isInteger(productId) || selectedIds.has(productId)) return;
                selectedIds.add(productId);
                selected.push(item);
            };

            sameCategory.forEach(pushIfNew);

            const needsNearbyFallback = recommendationMode === 'mixed-nearby-categories'
                ? selected.length < Math.max(4, minSameCategory)
                : selected.length < minSameCategory;

            if (needsNearbyFallback && currentCategoryId > 0 && sortedCategories.length > 0) {
                const categoryIndex = sortedCategories.findIndex(cat => Number(cat?.id) === currentCategoryId);
                if (categoryIndex >= 0) {
                    const nearbyCategoryIds = [];
                    for (let step = 1; step <= nearbyWindow; step += 1) {
                        const left = sortedCategories[categoryIndex - step];
                        const right = sortedCategories[categoryIndex + step];
                        if (left?.id) nearbyCategoryIds.push(Number(left.id));
                        if (right?.id) nearbyCategoryIds.push(Number(right.id));
                    }

                    allProductsList.forEach(item => {
                        const itemCategoryId = Number(item?.categoryId || item?.category?.id || 0);
                        if (nearbyCategoryIds.includes(itemCategoryId)) {
                            pushIfNew(item);
                        }
                    });
                }
            }

            allProductsList.forEach(item => {
                if (Number(item?.id) !== Number(p?.id)) {
                    pushIfNew(item);
                }
            });

            setRelated(selected);
        });

        const silentSyncProduct = () => {
            getProduct(id, { skipCache: true })
                .then(fresh => {
                    if (!fresh || isCancelled) return;
                    setProduct(prev => {
                        if (JSON.stringify(prev) === JSON.stringify(fresh)) return prev;
                        return fresh;
                    });
                })
                .catch(() => {});
        };

        const syncInterval = setInterval(silentSyncProduct, 6000);
        window.addEventListener('luxvono:realtime-catalog-sync', silentSyncProduct);

        return () => {
            isCancelled = true;
            clearInterval(syncInterval);
            window.removeEventListener('luxvono:realtime-catalog-sync', silentSyncProduct);
        };
    }, [id, storeSettings?.recommendationMode, storeSettings?.recommendationMinSameCategory, storeSettings?.recommendationNearbyCategoryWindow, getWholesaleTiers]);

    useEffect(() => {
        if (!product?.id || typeof window === 'undefined') return;
        try {
            const current = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
            const next = [Number(product.id), ...(Array.isArray(current) ? current.map(Number) : [])]
                .filter(Number.isFinite)
                .filter((value, index, arr) => arr.indexOf(value) === index)
                .slice(0, 24);
            window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
            if (allProducts && allProducts.length > 0) {
                const viewed = next
                    .filter(pid => Number(pid) !== Number(product.id))
                    .map(pid => allProducts.find(p => Number(p.id) === Number(pid)))
                    .filter(Boolean);
                setRecentlyViewedList(viewed);
            }
        } catch (_) {
            // Ignore persistence errors to avoid breaking product navigation.
        }
    }, [product?.id, allProducts]);

    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl || !mediaList.length) return;
        
        const currentMedia = mediaList[activeMediaIndex] || '';
        const isVideo = currentMedia.includes('.mp4') || currentMedia.includes('.m3u8') || currentMedia.includes('video');
        if (!isVideo) return;

        const src = resolveImageUrl(currentMedia);
        
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.playsInline = true;

        const triggerAutoplay = () => {
            try {
                const p = videoEl.play();
                if (p && typeof p.catch === 'function') {
                    p.catch(() => {
                        const resumeOnInteraction = () => {
                            if (videoRef.current) {
                                videoRef.current.muted = true;
                                videoRef.current.play().catch(() => {});
                            }
                            window.removeEventListener('click', resumeOnInteraction);
                            window.removeEventListener('touchstart', resumeOnInteraction);
                            window.removeEventListener('scroll', resumeOnInteraction);
                        };
                        window.addEventListener('click', resumeOnInteraction, { once: true, passive: true });
                        window.addEventListener('touchstart', resumeOnInteraction, { once: true, passive: true });
                        window.addEventListener('scroll', resumeOnInteraction, { once: true, passive: true });
                    });
                }
            } catch (_) {}
        };

        if (src.includes('.m3u8')) {
            if (Hls.isSupported()) {
                const hls = new Hls({ enableWorker: false });
                hlsRef.current = hls;
                hls.loadSource(src);
                hls.attachMedia(videoEl);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    triggerAutoplay();
                });
            } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
                videoEl.src = src;
                triggerAutoplay();
            }
        } else {
            videoEl.src = src;
            triggerAutoplay();
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [activeMediaIndex, mediaList, resolveImageUrl]);

    // Update selected variant when attributes change
    useEffect(() => {
        if (!product || !product.variants || product.variants.length === 0) return;
        
        const matchedVariant = product.variants.find(v => {
            return Object.entries(selectedAttributes).every(([key, val]) => ['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(key) || (v.attributes && v.attributes[key] === val));
        });
        
        setSelectedVariant(matchedVariant || product.variants[0]);
        // Extract variant specific image (either from variant.imageUrl, variant.images, or from attribute detailed values)
        let variantImg = matchedVariant?.imageUrl;
        
        if (!variantImg && product.attributes) {
            // First try to find image from matchedVariant
            if (matchedVariant) {
                product.attributes.forEach(attr => {
                    if (attr.detailedValues) {
                        const match = attr.detailedValues.find(d => d.value === matchedVariant.attributes[attr.name]);
                        if (match && match.imageUrl) variantImg = match.imageUrl;
                    }
                });
            }
            
            // If still no image, try to find an image from ANY selected attribute
            if (!variantImg) {
                for (const attr of product.attributes) {
                    if (attr.detailedValues && selectedAttributes[attr.name]) {
                        const match = attr.detailedValues.find(d => d.value === selectedAttributes[attr.name]);
                        if (match && match.imageUrl) {
                            variantImg = match.imageUrl;
                            break; // prioritize the first one we find (usually Color is first)
                        }
                    }
                }
            }
        }

        // Update images if variant has specific images
        const productPageVideos = Array.isArray(product.videos)
            ? product.videos.filter(v => v.showInProductPage !== false).map(v => v.videoUrl).filter(Boolean)
            : (product.videoUrl ? [product.videoUrl] : []);
        const allVideos = [
            ...(product?.typeMetadata?.catwalkVideoUrl ? [product.typeMetadata.catwalkVideoUrl] : []),
            ...productPageVideos
        ].filter(Boolean);

        let finalMediaList = [];
        if (matchedVariant && matchedVariant.images && matchedVariant.images.length > 0) {
            finalMediaList = [...new Set([...matchedVariant.images, ...allVideos])];
        } else if (variantImg) {
            // Prepend the specific variant image to the product's image gallery
            const imageGallery = Array.isArray(product.images) && product.images.length > 0 ? product.images : [product.primaryImageUrl || product.image];
            const finalMedia = [variantImg, ...imageGallery, ...allVideos].filter(Boolean);
            finalMediaList = [...new Set(finalMedia)];
        } else if (product) {
            // Reset to product images first, followed by videos
            const imageGallery = Array.isArray(product.images) && product.images.length > 0 ? product.images : [product.primaryImageUrl || product.image];
            const finalMedia = [...imageGallery, ...allVideos].filter(Boolean);
            finalMediaList = finalMedia.length ? [...new Set(finalMedia)] : ['/placeholder.png'];
        }

        setMediaList(finalMediaList);

        // Smart gallery auto-focus on selected color image
        if (product && product.attributes) {
            const colorAttr = product.attributes.find(a => a.name.toLowerCase().includes('color') || a.name.includes('اللون') || a.name.includes('لون'));
            if (colorAttr && selectedAttributes[colorAttr.name]) {
                const selectedColorVal = selectedAttributes[colorAttr.name].toLowerCase();
                const colorValObj = colorAttr.detailedValues?.find(v => v.value === selectedAttributes[colorAttr.name]);
                const attrImageUrl = colorValObj?.imageUrl ? colorValObj.imageUrl.toLowerCase() : null;

                const matchedImgIndex = finalMediaList.findIndex(imgUrl => {
                    const normalizedImgUrl = String(imgUrl || '').toLowerCase();
                    if (normalizedImgUrl.endsWith('.mp4') || normalizedImgUrl.endsWith('.m3u8')) return false;
                    // Match by database-configured image URL first
                    if (attrImageUrl && (normalizedImgUrl === attrImageUrl || normalizedImgUrl.endsWith(attrImageUrl))) {
                        return true;
                    }
                    if (normalizedImgUrl.includes(selectedColorVal)) {
                        return true;
                    }
                    // Match by individual words (e.g. 'ruby' or 'red' in 'roja_perfume_red.png')
                    const words = selectedColorVal.split(/[\s_-]+/);
                    return words.some(word => word.length > 2 && normalizedImgUrl.includes(word));
                });

                if (matchedImgIndex >= 0) {
                    setActiveMediaIndex(matchedImgIndex);
                } else {
                    setActiveMediaIndex(0);
                }
            } else {
                setActiveMediaIndex(0);
            }
        } else {
            setActiveMediaIndex(0);
        }
    }, [selectedAttributes, product]);

    // Auto-scroll active thumbnail into view when activeMediaIndex changes
    useEffect(() => {
        if (!thumbStripRef.current) return;
        const activeBtn = thumbStripRef.current.querySelector('.product-gallery-thumb-btn.active');
        if (activeBtn && typeof activeBtn.scrollIntoView === 'function') {
            try {
                activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            } catch (_) {}
        }
    }, [activeMediaIndex]);

    if (loading || !product) return <Spinner />;

    const activeMedia = mediaList[activeMediaIndex] || '/placeholder.png';
    const baseDisplayPrice = Number(selectedVariant?.salePrice || selectedVariant?.price || product.price || 0);
    const wholesaleTiers = getWholesaleTiers(product);
    const inventoryRules = getInventoryRules(product);

    const activeWholesaleTier = wholesaleTiers.find(t => {
        const min = Number(t.minQty) || 1;
        const max = t.maxQty && Number(t.maxQty) > 0 ? Number(t.maxQty) : Infinity;
        return quantity >= min && quantity <= max;
    });

    const displayPrice = activeWholesaleTier ? Number(activeWholesaleTier.unitPrice) : baseDisplayPrice;
    const displayComparePrice = activeWholesaleTier && baseDisplayPrice > displayPrice 
        ? baseDisplayPrice 
        : Number(selectedVariant?.compareAtPrice || selectedVariant?.comparePrice || selectedVariant?.oldPrice || product.compareAtPrice || product.comparePrice || product.oldPrice || product.originalPrice || 0);
    const rawDiscount = product.discountPercentage || product.discountPercent || product.discount;
    const discountPercent = displayComparePrice > displayPrice && displayPrice > 0
        ? Math.round(((displayComparePrice - displayPrice) / displayComparePrice) * 100)
        : (rawDiscount ? Number(rawDiscount) : null);
    const bulkSavings = activeWholesaleTier && baseDisplayPrice > displayPrice ? (baseDisplayPrice - displayPrice) * quantity : 0;
    const stockCount = Number(product?.stock >= 99999 ? 99999 : (selectedVariant?.quantity ?? product?.stock ?? 0));
    const displayBrand = product.brandName || product.brand || product.category || '';
    const displayName = (isRTL() ? (product?.nameAr || product?.name) : (product?.nameEn || product?.name)) || product?.name || '';
    const displayDesc = (isRTL() ? (product?.descriptionAr || product?.description) : (product?.descriptionEn || product?.description)) || product?.description || '';

    const isOutOfStock = (inventoryRules?.trackInventory !== false && product?.stock < 99999) &&
        ((selectedVariant && Number(selectedVariant.quantity) <= 0) || (!selectedVariant && stockCount <= 0));

    const primaryVideoRaw = (Array.isArray(product?.videos) && product.videos.find(v => v.showInProductPage !== false)?.videoUrl)
        || product?.videoUrl
        || product?.typeMetadata?.catwalkVideoUrl
        || null;
    const primaryVideoUrl = primaryVideoRaw ? resolveImageUrl(primaryVideoRaw) : null;

    const handleShareProduct = () => {
        if (typeof window === 'undefined') return;
        const shareUrl = window.location.href;
        if (navigator.share) {
            navigator.share({
                title: displayName,
                text: displayName,
                url: shareUrl
            }).catch(() => {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setShareCopiedToast(true);
                setTimeout(() => setShareCopiedToast(false), 2200);
            }).catch(() => {});
        }
    };

    const handleAddToCart = () => {
        if (!product || isSubmittingCart || isOutOfStock) return;
        if (product.attributes && product.attributes.length > 0) {
            const visibleAttrs = product.attributes.filter(attr => !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name));
            const missing = visibleAttrs.filter(attr => !selectedAttributes[attr.name]).map(attr => attr.name);
            if (missing.length > 0) {
                setValidationErrors(missing);
                const el = document.querySelector('.variant-group') || document.querySelector('.product-detail-specifications') || document.querySelector('.product-detail-price');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }
        setValidationErrors([]);
        setIsSubmittingCart(true);
        const payload = selectedVariant ? { ...product, selectedVariant } : { ...product };
        payload.quantity = quantity;
        if (activeWholesaleTier) {
            payload.wholesalePrice = Number(activeWholesaleTier.unitPrice);
            payload.price = Number(activeWholesaleTier.unitPrice);
        }
        onAddToCart?.(payload);
        setCartAddedToast(true);
        setTimeout(() => {
            setIsSubmittingCart(false);
        }, 500);
        setTimeout(() => {
            setCartAddedToast(false);
        }, 2000);
    };

    const handleBuyNow = () => {
        if (!product || isSubmittingBuy || isOutOfStock) return;
        if (product.attributes && product.attributes.length > 0) {
            const visibleAttrs = product.attributes.filter(attr => !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name));
            const missing = visibleAttrs.filter(attr => !selectedAttributes[attr.name]).map(attr => attr.name);
            if (missing.length > 0) {
                setValidationErrors(missing);
                const el = document.querySelector('.variant-group') || document.querySelector('.product-detail-specifications') || document.querySelector('.product-detail-price');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }
        setValidationErrors([]);
        setIsSubmittingBuy(true);
        const payload = selectedVariant ? { ...product, selectedVariant } : { ...product };
        payload.quantity = quantity;
        if (activeWholesaleTier) {
            payload.wholesalePrice = Number(activeWholesaleTier.unitPrice);
            payload.price = Number(activeWholesaleTier.unitPrice);
        }
        onAddToCart?.(payload);
        navigate('/checkout');
    };

    const handleAskSeller = () => {
        if (!product) return;
        const mainImage = resolveStorefrontImageUrl(product.primaryImageUrl || product.imageUrl || (product.images && (product.images[0]?.url || product.images[0])) || '');
        const targetVendorId = product.vendorId || product.vendor?.id || product.sellerId || 1;
        window.dispatchEvent(new CustomEvent('open-seller-chat', {
            detail: {
                productId: product.id,
                productName: isRTL() ? (product.nameAr || product.name) : (product.nameEn || product.name),
                productPrice: product.price,
                productCurrency: product.currency || 'USD',
                productImage: mainImage,
                vendorId: targetVendorId,
                sellerId: targetVendorId,
                sellerName: product.vendorName || (isRTL() ? 'تاجر معتمد لدى LUXVONO' : 'LUXVONO Verified Partner'),
                sku: product.sku
            }
        }));
    };
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    const deliveryStart = new Date();
    deliveryStart.setDate(deliveryStart.getDate() + 3);
    const deliveryEnd = new Date();
    deliveryEnd.setDate(deliveryEnd.getDate() + 6);
    const deliveryRange = `${formatter.format(deliveryStart)} - ${formatter.format(deliveryEnd)}`;
    const frequentlyBoughtTogether = related.slice(0, 4);
    const completeTheLook = related.slice(4, 8);

    return (
        <div className="page-product">
            <style>
                {`
                    @keyframes productGalleryFade {
                        from { opacity: 0; transform: scale(1.012); }
                        to { opacity: 1; transform: scale(1); }
                    .product-gallery-main {
                        position: relative;
                        width: 100%;
                        aspect-ratio: 1 / 1;
                        max-height: 540px;
                        border-radius: 20px;
                        overflow: hidden;
                        background: #ffffff;
                        border: 1px solid #e5e7eb;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .product-gallery-main-image {
                        width: 100%;
                        height: 100%;
                        animation: productGalleryFade 240ms ease;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #ffffff;
                    }
                    .product-gallery-counter {
                        position: absolute;
                        bottom: 16px;
                        right: 16px;
                        background: rgba(17, 17, 17, 0.72);
                        color: #ffffff;
                        padding: 4px 10px;
                        border-radius: 999px;
                        font-size: 11px;
                        font-weight: 700;
                        letter-spacing: 0.5px;
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        z-index: 5;
                        direction: ltr !important;
                    }
                    [dir="rtl"] .product-gallery-counter {
                        right: auto !important;
                        left: 16px !important;
                    }
                    .product-gallery-arrow {
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                        width: 44px;
                        height: 44px;
                        border-radius: 50%;
                        border: 1px solid #EAEAEA;
                        background: rgba(255, 255, 255, 0.95);
                        color: #111111;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1);
                        z-index: 2;
                        backdrop-filter: blur(12px);
                        -webkit-backdrop-filter: blur(12px);
                        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
                    }
                    .product-gallery-arrow:hover {
                        border-color: var(--accent);
                        color: var(--accent);
                        box-shadow: 0 6px 20px rgba(95, 52, 214, 0.15);
                    }
                    .product-gallery-arrow-left {
                        left: 12px;
                    }
                    .product-gallery-arrow-right {
                        right: 12px;
                    }
                    .product-gallery-thumbs {
                        display: flex;
                        gap: 10px;
                        overflow-x: auto;
                        padding: 4px 2px 8px;
                        scrollbar-width: thin;
                    }
                    .product-gallery-thumb-btn {
                        width: 82px;
                        height: 82px;
                        flex-shrink: 0;
                        border-radius: 16px;
                        overflow: hidden;
                        border: 2px solid #EAEAEA;
                        background: #F8F8F8;
                        cursor: pointer;
                        padding: 0;
                        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1);
                    }
                    .product-gallery-thumb-btn.active {
                        border-color: #111111;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    }
                    .product-gallery-thumb-btn img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        object-position: center;
                        display: block;
                    }
                    @media (max-width: 1023px) {
                        .product-gallery-main {
                            border-radius: 16px;
                        }
                    }
                    @media (max-width: 768px) {
                        .page-product {
                            padding-top: 10px !important;
                            position: relative !important;
                        }
                        .back-link {
                            position: absolute !important;
                            top: 16px !important;
                            left: 16px !important;
                            z-index: 10 !important;
                            background: rgba(255, 255, 255, 0.8) !important;
                            backdrop-filter: blur(8px) !important;
                            -webkit-backdrop-filter: blur(8px) !important;
                            border: 1px solid rgba(0,0,0,0.06) !important;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
                            margin: 0 !important;
                            width: 36px !important;
                            height: 36px !important;
                            padding: 0 !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                            border-radius: 50% !important;
                            font-size: 0 !important;
                            color: #111111 !important;
                        }
                        [dir="rtl"] .back-link {
                            left: auto !important;
                            right: 16px !important;
                            transform: scaleX(-1) !important;
                        }
                        .product-gallery-arrow {
                            display: none;
                        }
                        .product-detail {
                            margin-top: 0 !important;
                        }
                        .product-gallery-main {
                            border-radius: 0 !important;
                            border: none !important;
                            background: #ffffff !important;
                            overflow: hidden !important;
                            width: calc(100% + 32px) !important;
                            margin: 0 -16px !important;
                        }
                        .product-gallery-thumbs {
                            display: flex !important;
                            gap: 8px !important;
                            overflow-x: auto !important;
                            -webkit-overflow-scrolling: touch !important;
                            padding: 8px 0 !important;
                            margin: 0 !important;
                            scrollbar-width: none !important;
                        }
                        .product-gallery-thumbs::-webkit-scrollbar {
                            display: none !important;
                        }
                        .product-gallery-thumb-btn {
                            width: 56px !important;
                            height: 56px !important;
                            border-radius: 8px !important;
                            border: 2px solid transparent !important;
                            flex-shrink: 0 !important;
                            transition: all 0.25s ease !important;
                            background: #F8F8F8 !important;
                            padding: 0 !important;
                            overflow: hidden !important;
                            box-sizing: border-box !important;
                        }
                        .product-gallery-thumb-btn.active {
                            border-color: #111111 !important;
                            transform: scale(1.05) !important;
                            box-shadow: 0 4px 10px rgba(0,0,0,0.06) !important;
                        }
                        .product-detail-media-gallery {
                            margin: 0 !important;
                            width: 100% !important;
                            gap: 10px !important;
                        }
                        .product-detail-info {
                            padding: 16px 16px !important;
                            border-radius: 0 !important;
                            border: none !important;
                            background: transparent !important;
                            box-shadow: none !important;
                        }
                        .product-detail-name {
                            font-size: 26px !important;
                            margin: 8px 0 8px !important;
                            line-height: 1.2 !important;
                        }
                        .product-detail-price {
                            font-size: 22px !important;
                        }
                        .product-detail-actions {
                            display: flex !important;
                            gap: 10px !important;
                            width: 100% !important;
                        }
                        .product-detail-actions .btn-primary {
                            flex: 1 !important;
                            width: 100% !important;
                            justify-content: center !important;
                            min-height: 48px !important;
                        }
                        .product-detail-actions .btn-icon {
                            width: 48px !important;
                            height: 48px !important;
                            flex-shrink: 0 !important;
                        }
                    }

                    /* Premium Color Swatches styling */
                    .lux-swatch-btn {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        border: 1px solid rgba(0, 0, 0, 0.12);
                        cursor: pointer;
                        padding: 0;
                        position: relative;
                        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                        box-shadow: inset 0 2px 4px rgba(0,0,0,0.06);
                    }
                    .lux-swatch-btn:hover {
                        transform: scale(1.12);
                        box-shadow: 0 4px 12px rgba(0,0,0,0.12), inset 0 2px 4px rgba(0,0,0,0.06);
                    }
                    .lux-swatch-btn.selected {
                        outline: 2.5px solid #000000;
                        outline-offset: 3px;
                    }
                    
                    /* Premium Size Chips styling */
                    .lux-size-btn {
                        padding: 10px 20px;
                        border-radius: 8px;
                        border: 1px solid #E5E5E5;
                        background: #FFFFFF;
                        color: #222222;
                        font-weight: 600;
                        font-size: 13px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        cursor: pointer;
                        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .lux-size-btn:hover {
                        border-color: #111111;
                        transform: translateY(-1px);
                        box-shadow: 0 4px 10px rgba(0,0,0,0.05);
                    }
                    .lux-size-btn.selected {
                        border-color: #000000;
                        background: #000000;
                        color: #ffffff;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                    }
                    
                    /* Perfume Scent Pyramid Styles */
                    .lux-pyramid-container {
                        background: linear-gradient(180deg, rgba(184, 145, 84, 0.03) 0%, rgba(255, 255, 255, 0) 100%);
                        border: 1px solid rgba(184, 145, 84, 0.15);
                        border-radius: 16px;
                        padding: 20px;
                        margin: 20px 0;
                    }
                    .lux-pyramid-level {
                        padding: 12px 16px;
                        border-radius: 12px;
                        background: #FFFFFF;
                        border: 1px solid #EAEAEA;
                        margin-bottom: 10px;
                        transition: all 0.3s ease;
                    }
                    .lux-pyramid-level:hover {
                        border-color: var(--lux-gold);
                        transform: translateY(-1.5px);
                        box-shadow: 0 6px 15px rgba(184, 145, 84, 0.06);
                    }
                    .lux-pyramid-title {
                        font-weight: 700;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.08em;
                        color: var(--lux-gold);
                        margin-bottom: 4px;
                    }
                    .lux-pyramid-notes {
                        font-size: 13px;
                        color: #222222;
                    }

                    /* Car Spec Table & Calculator Styles */
                    .lux-car-specs-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                        margin: 20px 0;
                    }
                    .lux-car-spec-card {
                        background: #F8F8F8;
                        border: 1px solid #EAEAEA;
                        border-radius: 12px;
                        padding: 12px 16px;
                        display: flex;
                        flex-direction: column;
                    }
                    .lux-car-spec-label {
                        font-size: 11px;
                        color: #666666;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        margin-bottom: 2px;
                    }
                    .lux-car-spec-value {
                        font-weight: 600;
                        font-size: 13px;
                        color: #111111;
                    }
                    .lux-car-calculator {
                        background: #111111;
                        color: #FFFFFF;
                        border-radius: 16px;
                        padding: 20px;
                        margin: 20px 0;
                        border: 1px solid #222222;
                    }
                    .lux-car-calc-title {
                        font-weight: 700;
                        font-size: 13.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        color: var(--lux-gold);
                        margin-bottom: 12px;
                    }

                    /* Tech Spec Styles */
                    .lux-tech-specs {
                        border: 1px solid #EAEAEA;
                        border-radius: 16px;
                        overflow: hidden;
                        margin: 20px 0;
                    }
                    .lux-tech-spec-row {
                        display: flex;
                        padding: 12px 16px;
                        border-bottom: 1px solid #EAEAEA;
                        font-size: 13px;
                    }
                    .lux-tech-spec-row:last-child {
                        border-bottom: none;
                    }
                    .lux-tech-spec-label {
                        width: 140px;
                        font-weight: 600;
                        color: #555555;
                    }
                    .lux-tech-spec-value {
                        color: #111111;
                        flex: 1;
                    }
                `}
            </style>
            <div className="container product-detail-container" style={{ paddingTop: '0px', width: '100%', maxWidth: '1320px', margin: '0 auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0 8px 0' }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/');
                            }
                        }} 
                        className="back-link" 
                        style={{ 
                            margin: 0, 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            padding: '6px 0', 
                            color: 'inherit', 
                            fontSize: '14px', 
                            fontWeight: '600' 
                        }}
                        aria-label={t('BACK') || 'رجوع'}
                    >
                        {isRTL() ? <ChevronRight size={18} /> : <ChevronLeft size={18} />} 
                        <span>{t('BACK') || (isRTL() ? 'رجوع' : 'Back')}</span>
                    </button>
                </div>

                {/* Slot: top */}
                <DynamicSectionsRenderer
                    page="product-page"
                    position="top"
                    allProducts={allProducts}
                    allCategories={categories}
                    onAddToCart={onAddToCart}
                    storeSettings={storeSettings}
                />

                <div className="product-detail">
                    <div className="product-detail-media-gallery" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #EAEAEA', paddingBottom: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <button 
                                type="button"
                                onClick={() => setActiveTab('gallery')} 
                                style={{ 
                                    padding: '8px 18px', borderRadius: '12px', border: '1px solid #EAEAEA',
                                    background: activeTab === 'gallery' ? '#111111' : 'transparent',
                                    color: activeTab === 'gallery' ? '#fff' : '#666666',
                                    fontWeight: '700', fontSize: '12.5px',
                                    cursor: 'pointer', transition: 'all 0.25s ease',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                }}
                            >
                                <span>{isRTL() ? '📷 الصور والمعرض' : '📷 Gallery'}</span>
                            </button>

                            <button 
                                type="button"
                                onClick={() => setActiveTab('360')} 
                                style={{ 
                                    padding: '8px 18px', borderRadius: '12px', border: '1px solid #EAEAEA',
                                    background: activeTab === '360' ? '#111111' : 'transparent',
                                    color: activeTab === '360' ? '#ffffff' : '#666666',
                                    fontWeight: '700', fontSize: '12.5px',
                                    cursor: 'pointer', transition: 'all 0.25s ease',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    borderColor: activeTab === '360' ? '#111111' : '#EAEAEA'
                                }}
                            >
                                <RotateCw size={14} className={activeTab === '360' ? 'spin-slow' : ''} />
                                <span>{isRTL() ? '🔄 تدوير 360° تفاعلي' : '🔄 360° Interactive'}</span>
                            </button>

                            {product.glbUrl && (
                                <button 
                                    type="button"
                                    onClick={() => setActiveTab('3d')} 
                                    style={{ 
                                        padding: '8px 18px', borderRadius: '12px', border: '1px solid #EAEAEA',
                                        background: activeTab === '3d' ? '#111111' : 'transparent',
                                        color: activeTab === '3d' ? '#fff' : '#666666',
                                        fontWeight: '700', fontSize: '12.5px',
                                        cursor: 'pointer', transition: 'all 0.25s ease',
                                        display: 'flex', alignItems: 'center', gap: '6px'
                                    }}
                                >
                                    <BoxIcon size={14} />
                                    <span>{isRTL() ? '📦 مجسم 3D' : '📦 3D Model'}</span>
                                </button>
                            )}
                        </div>

                        {activeTab === '360' ? (
                            <div style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: '22px', overflow: 'hidden' }}>
                                <RealProduct360Viewer 
                                    frames={product.images && product.images.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : [])}
                                    initialImage={resolveImageUrl(activeMedia)}
                                    glbUrl={null}
                                    productName={product.nameAr || product.name}
                                    isRTL={isRTL()}
                                    scaleFactor={product.scaleFactor || 1.0}
                                    lightingPreset={product.lightingPreset || 'studio'}
                                />
                            </div>
                        ) : activeTab === '3d' && product.glbUrl ? (
                            <div style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: '22px', overflow: 'hidden' }}>
                                <RealProduct360Viewer 
                                    frames={product.images && product.images.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : [])}
                                    initialImage={resolveImageUrl(activeMedia)}
                                    glbUrl={product.glbUrl}
                                    productName={product.nameAr || product.name}
                                    isRTL={isRTL()}
                                    scaleFactor={product.scaleFactor || 1.0}
                                    lightingPreset={product.lightingPreset || 'studio'}
                                />
                            </div>
                        ) : (
                            <div
                                className="product-gallery-main"
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', maxHeight: '460px', borderRadius: '20px', overflow: 'hidden', background: '#ffffff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                            {mediaList.length > 1 && (
                                <div 
                                    className="product-gallery-counter"
                                    style={{
                                        position: 'absolute',
                                        bottom: '14px',
                                        right: isRTL() ? 'auto' : '14px',
                                        left: isRTL() ? '14px' : 'auto',
                                        background: 'rgba(17, 24, 39, 0.72)',
                                        color: '#ffffff',
                                        padding: '4px 11px',
                                        borderRadius: '999px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        zIndex: 20,
                                        direction: 'ltr',
                                        backdropFilter: 'blur(6px)',
                                        WebkitBackdropFilter: 'blur(6px)'
                                    }}
                                >
                                    {activeMediaIndex + 1}/{mediaList.length}
                                </div>
                            )}
                            {/* Top-End Overlay Action Buttons (Zoom & Share — matching reference layout) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '14px',
                                    right: isRTL() ? 'auto' : '14px',
                                    left: isRTL() ? '14px' : 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    zIndex: 25
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsLightboxOpen(true);
                                    }}
                                    aria-label={isRTL() ? 'تكبير الصورة' : 'Full-screen image zoom'}
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        background: 'rgba(17, 17, 17, 0.45)',
                                        color: '#ffffff',
                                        border: '1px solid rgba(255,255,255,0.22)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        backdropFilter: 'blur(8px)',
                                        WebkitBackdropFilter: 'blur(8px)'
                                    }}
                                >
                                    <ZoomIn size={17} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleShareProduct();
                                    }}
                                    aria-label={isRTL() ? 'مشاركة المنتج' : 'Share product'}
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        background: 'rgba(17, 17, 17, 0.45)',
                                        color: '#ffffff',
                                        border: '1px solid rgba(255,255,255,0.22)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        backdropFilter: 'blur(8px)',
                                        WebkitBackdropFilter: 'blur(8px)'
                                    }}
                                >
                                    <Share2 size={16} />
                                </button>
                            </div>
                            {mediaList.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        className="product-gallery-arrow product-gallery-arrow-prev"
                                        onClick={goPrev}
                                        aria-label="Previous image"
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            left: '10px',
                                            width: '38px',
                                            height: '38px',
                                            borderRadius: '50%',
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid rgba(0, 0, 0, 0.15)',
                                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#111827',
                                            cursor: 'pointer',
                                            zIndex: 35
                                        }}
                                    >
                                        <ChevronLeft size={22} />
                                    </button>
                                    <button
                                        type="button"
                                        className="product-gallery-arrow product-gallery-arrow-next"
                                        onClick={goNext}
                                        aria-label="Next image"
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            right: '10px',
                                            width: '38px',
                                            height: '38px',
                                            borderRadius: '50%',
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            border: '1px solid rgba(0, 0, 0, 0.15)',
                                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#111827',
                                            cursor: 'pointer',
                                            zIndex: 35
                                        }}
                                    >
                                        <ChevronRight size={22} />
                                    </button>
                                </>
                            )}
                            {activeMedia && (activeMedia.endsWith('.mp4') || activeMedia.endsWith('.m3u8')) ? (
                                <div 
                                    className="product-gallery-main-image" 
                                    style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer', background: '#000' }}
                                    onClick={() => {
                                        if (videoRef.current) {
                                            if (videoRef.current.paused) {
                                                videoRef.current.play().catch(() => {});
                                            } else {
                                                videoRef.current.pause();
                                            }
                                        }
                                    }}
                                >
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        muted={isMuted}
                                        loop
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000000', display: 'block' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                                        style={{
                                            position: 'absolute',
                                            bottom: '12px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(17, 24, 39, 0.92)',
                                            color: '#ffffff',
                                            border: '1.5px solid rgba(255, 255, 255, 0.4)',
                                            borderRadius: '999px',
                                            padding: '7px 16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '12.5px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            zIndex: 30,
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                                            backdropFilter: 'blur(8px)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        aria-label={isMuted ? "Unmute" : "Mute"}
                                    >
                                        {isMuted ? <VolumeX size={17} /> : <Volume2 size={17} />}
                                        <span>{isMuted ? t('UNMUTE') : t('MUTE')}</span>
                                    </button>
                                </div>
                            ) : (
                                <div 
                                    className="product-gallery-main-image" 
                                    onClick={() => setIsLightboxOpen(true)}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        background: '#ffffff', 
                                        position: 'relative', 
                                        overflow: 'hidden', 
                                        cursor: 'zoom-in' 
                                    }}
                                >
                                    <img
                                        key={`${id}-${activeMediaIndex}`}
                                        src={resolveImageUrl(activeMedia)}
                                        alt={`${product.name} media ${activeMediaIndex + 1}`}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect width="400" height="400" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-family="sans-serif" font-size="18" font-weight="bold">LUXVONO</text></svg>';
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'contain', 
                                            display: 'block', 
                                            transform: isZoomed ? 'scale(1.8)' : 'scale(1)', 
                                            transition: 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)' 
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        )}
                        {activeTab === 'gallery' && mediaList.length > 1 && (
                            <div 
                                ref={thumbStripRef}
                                className="product-gallery-thumbs"
                                onMouseDown={(e) => {
                                    setIsDraggingThumb(true);
                                    if (thumbStripRef.current) {
                                        setThumbStartX(e.pageX - thumbStripRef.current.offsetLeft);
                                        setThumbScrollLeft(thumbStripRef.current.scrollLeft);
                                    }
                                }}
                                onMouseLeave={() => setIsDraggingThumb(false)}
                                onMouseUp={() => setIsDraggingThumb(false)}
                                onMouseMove={(e) => {
                                    if (!isDraggingThumb || !thumbStripRef.current) return;
                                    e.preventDefault();
                                    const x = e.pageX - thumbStripRef.current.offsetLeft;
                                    const walk = (x - thumbStartX) * 1.5;
                                    thumbStripRef.current.scrollLeft = thumbScrollLeft - walk;
                                }}
                                style={{ 
                                    width: '100%', 
                                    marginTop: '10px',
                                    marginBottom: '16px',
                                    scrollBehavior: isDraggingThumb ? 'auto' : 'smooth', 
                                    overflowX: 'auto', 
                                    WebkitOverflowScrolling: 'touch',
                                    display: 'flex', 
                                    gap: '8px',
                                    cursor: isDraggingThumb ? 'grabbing' : 'grab',
                                    userSelect: 'none',
                                    padding: '4px 2px',
                                    scrollbarWidth: 'none'
                                }}
                            >
                                {mediaList.map((m, i) => (
                                    <button
                                        type="button"
                                        key={i}
                                        onClick={() => {
                                            if (!isDraggingThumb) goToMedia(i);
                                        }}
                                        className={`product-gallery-thumb-btn ${i === activeMediaIndex ? 'active' : ''}`}
                                        aria-label={`Show image ${i + 1}`}
                                        style={{ 
                                            width: '60px',
                                            height: '60px',
                                            flexShrink: 0,
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            border: `2px solid ${i === activeMediaIndex ? '#e02424' : '#e5e7eb'}`,
                                            background: '#ffffff',
                                            padding: 0,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: i === activeMediaIndex ? '0 2px 8px rgba(224, 36, 36, 0.25)' : 'none'
                                        }}
                                    >
                                        {m.endsWith('.mp4') || m.endsWith('.m3u8') ? (
                                            <div style={{ width: '100%', height: '100%', background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                                                <span style={{ fontSize: '16px' }}>▶</span>
                                            </div>
                                        ) : (
                                            <img
                                                src={resolveImageUrl(m)}
                                                alt={`${product.name} thumbnail ${i + 1}`}
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-family="sans-serif" font-size="11" font-weight="bold">LUX</text></svg>';
                                                }}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* Verified Warehouse / Store Inventory Badge */}
                        <div style={{
                            marginTop: '20px',
                            background: '#ffffff',
                            border: '1px solid var(--border, #E5E5E5)',
                            borderRadius: '16px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '800' }}>
                                    {isRTL() ? 'مخزون موثق' : 'Verified Stock'}
                                </span>
                                <strong style={{ fontSize: '13.5px', color: '#111827', display: 'inline-block' }}>
                                    {storeSettings?.logoText || 'LUXVONO OFFICIAL'}
                                </strong>
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#6b7280' }}>
                                {isRTL() 
                                    ? 'مخزون معتمد • شحن فوري وتغليف فاخر أصلي من المستودعات الرسمية' 
                                    : 'Verified Inventory • Direct fulfillment from official luxury warehouses'}
                            </div>
                            <hr style={{ border: '0', borderTop: '1px solid #EAEAEA', margin: '0' }} />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', textAlign: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>
                                        100%
                                    </div>
                                    <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>{isRTL() ? 'أصلي ومضمون' : '100% Authentic'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>
                                        24-48h
                                    </div>
                                    <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>{isRTL() ? 'تجهيز سريع' : 'Fast Prep'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>
                                        4.9/5
                                    </div>
                                    <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>{t('STORE_RATING')}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '10px', background: 'rgba(184, 145, 84, 0.1)', color: 'var(--lux-gold)', padding: '3px 6px', borderRadius: '4px', fontWeight: '700' }}>
                                    {isRTL() ? 'فحص جودة معتمد' : 'Quality Inspected'}
                                </span>
                                <span style={{ fontSize: '10px', background: 'rgba(184, 145, 84, 0.1)', color: 'var(--lux-gold)', padding: '3px 6px', borderRadius: '4px', fontWeight: '700' }}>
                                    {isRTL() ? 'شحن مؤمن بالكامل' : 'Insured Shipping'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="product-detail-info">
                        {/* 1. Fast Delivery & Verified Stock Banner (matches reference layout hierarchy) */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <span style={{
                                background: isOutOfStock ? '#EF4444' : '#059669',
                                color: '#ffffff',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '11.5px',
                                fontWeight: 800,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                ✓ {isOutOfStock
                                    ? (isRTL() ? 'نفد من المخزون' : 'Out of Stock')
                                    : (isRTL() ? 'شحن مضمون بدون رسوم خفية' : 'Verified Stock • Direct Dispatch')}
                            </span>
                            <span style={{
                                color: '#059669',
                                fontSize: '12.5px',
                                fontWeight: 700
                            }}>
                                🚚 {isRTL() ? `أسرع توصيل: ${deliveryRange}` : `Fastest delivery: ${deliveryRange}`}
                            </span>
                        </div>

                        {/* 2. Product Name */}
                        <h1 className="product-detail-name" style={{ margin: '4px 0 8px 0' }}>{displayName}</h1>

                        {/* 3. Official Brand / Seller Quality Assurance Bar (matches reference screenshot) */}
                        <div
                            onClick={handleAskSeller}
                            role="button"
                            tabIndex={0}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: '#1c1917',
                                color: '#ffffff',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                                cursor: 'pointer',
                                fontSize: '12.5px',
                                fontWeight: 700
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span style={{ color: '#F59E0B' }}>✦</span>
                                <span>
                                    {isRTL()
                                        ? `المتجر الرسمي: ${product.vendorName || displayBrand || storeSettings?.logoText || 'LUXVONO'} • ضمان الأصالة والجودة`
                                        : `Brand Official Store: ${product.vendorName || displayBrand || storeSettings?.logoText || 'LUXVONO'} · Quality assurance`}
                                </span>
                            </div>
                            <span style={{ opacity: 0.85, fontSize: '14px', flexShrink: 0 }}>{isRTL() ? '‹' : '›'}</span>
                        </div>
                        
                        {/* 4. Rating & Verified Badge Row */}
                        <div className="product-detail-rating-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', margin: '4px 0 12px 0' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ background: '#4F46E5', color: '#ffffff', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
                                    ★ {product.category || (isRTL() ? 'مقتنى معتمد' : 'Star store')}
                                </span>
                            </div>
                            {Number(reviewStats?.totalReviews || 0) > 0 ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '13.5px', fontWeight: 800, color: '#D97706' }}>
                                        {Number(reviewStats.averageRating).toFixed(1)} ★
                                    </span>
                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                        | {isRTL()
                                            ? `${reviewStats.totalReviews} تقييم موثق`
                                            : `${reviewStats.totalReviews} reviews`}
                                    </span>
                                </div>
                            ) : (
                                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>
                                    ✦ {isRTL() ? 'إصدار أصلي معتمد' : 'Verified Authentic Release'}
                                </span>
                            )}
                        </div>

                        {/* 5. Price, Compare-At Price & Valid Discount Pill Row (placed prominently above fold) */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                            {displayComparePrice > displayPrice && (
                                <span style={{ textDecoration: 'line-through', color: '#111827', opacity: 0.65, fontSize: '1.15em', fontWeight: 700 }}>
                                    ${displayComparePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            )}
                            <h2 className="product-detail-price" style={{ margin: 0, fontSize: '1.95em', color: '#e02424', fontWeight: 900 }}>
                                ${displayPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                {activeWholesaleTier && <span style={{ fontSize: '0.45em', display: 'block', color: '#e02424', fontWeight: 600 }}>✦ {t('WHOLESALE_ACTIVE')} ({quantity} pcs)</span>}
                            </h2>
                            {discountPercent ? (
                                <span style={{ 
                                    background: 'rgba(224, 36, 36, 0.08)', 
                                    color: '#e02424', 
                                    border: '1px solid rgba(224, 36, 36, 0.35)',
                                    padding: '2px 8px', 
                                    borderRadius: '4px', 
                                    fontSize: '12.5px', 
                                    fontWeight: 800
                                }}>
                                    {discountPercent}% OFF
                                </span>
                            ) : (
                                <span style={{ 
                                    background: '#111111', 
                                    color: '#ffffff', 
                                    padding: '3px 9px', 
                                    borderRadius: '4px', 
                                    fontSize: '11.5px', 
                                    fontWeight: 700
                                }}>
                                    {t('LOWEST_PRICE_GUARANTEE')}
                                </span>
                            )}
                            {bulkSavings > 0 && (
                                <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.4)', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>
                                    Save ${(bulkSavings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            )}
                            {selectedVariant && selectedVariant.sku && (
                                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)', background: 'var(--bg-highlight)', padding: '3px 7px', borderRadius: '4px' }}>
                                    SKU: {selectedVariant.sku}
                                </span>
                            )}
                        </div>

                        {/* Reviews Accordion */}
                        <div className="product-detail-reviews-accordion" style={{ margin: '0 0 14px 0' }}>
                            <details className="lux-details-accordion">
                                <summary style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '10px 14px', background: 'var(--bg-highlight)', borderRadius: '12px', border: '1px solid var(--border)', fontWeight: 700, fontSize: '13px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>💬</span>
                                        <span>{t('CUSTOMER_REVIEWS')}</span>
                                    </div>
                                    <span style={{ fontSize: '11.5px', color: 'var(--lux-gold)' }}>{t('VIEW_REVIEWS')}</span>
                                </summary>
                                <div style={{ paddingTop: '12px' }}>
                                    <ProductReviews productId={product.id} storeSettings={storeSettings} />
                                </div>
                            </details>
                        </div>

                        <div className="product-detail-trust" style={{ marginTop: 0, marginBottom: '18px' }}>
                            <style>{`
                                @keyframes luxPulse {
                                    0% { opacity: 0.9; transform: scale(1); }
                                    50% { opacity: 1; transform: scale(1.03); box-shadow: 0 0 12px rgba(249, 115, 22, 0.4); }
                                    100% { opacity: 0.9; transform: scale(1); }
                                }
                                .pulse-badge {
                                    animation: luxPulse 2.2s infinite ease-in-out;
                                }
                            `}</style>
                            {(() => {
                                if (inventoryRules && inventoryRules.trackInventory === false) {
                                    const label = inventoryRules.availabilityLabel || 'In Stock';
                                    return (
                                        <span className="trust-badge" style={{ 
                                            background: 'rgba(16, 185, 129, 0.1)', 
                                            border: '1px solid rgba(16, 185, 129, 0.3)', 
                                            color: '#10B981', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            ✦ {label}
                                        </span>
                                    );
                                }
                                if (stockCount >= 99999) {
                                    return (
                                        <span className="trust-badge" style={{ 
                                            background: 'rgba(16, 185, 129, 0.1)', 
                                            border: '1px solid rgba(16, 185, 129, 0.3)', 
                                            color: '#10B981', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            ✦ {t('ALWAYS_AVAILABLE')}
                                        </span>
                                    );
                                } else if (stockCount === 0) {
                                    return (
                                        <span className="trust-badge" style={{ 
                                            background: 'rgba(239, 68, 68, 0.12)', 
                                            border: '1px solid rgba(239, 68, 68, 0.3)', 
                                            color: '#EF4444', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            🔴 {t('OUT_OF_STOCK')}
                                        </span>
                                    );
                                } else if (stockCount === 1) {
                                    return (
                                        <span className="trust-badge pulse-badge" style={{ 
                                            background: 'rgba(249, 115, 22, 0.12)', 
                                            border: '1px solid rgba(249, 115, 22, 0.4)', 
                                            color: '#F97316', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            🔥 {t('ONLY_1_LEFT')}
                                        </span>
                                    );
                                } else if (stockCount <= 10) {
                                    return (
                                        <span className="trust-badge pulse-badge" style={{ 
                                            background: 'rgba(249, 115, 22, 0.12)', 
                                            border: '1px solid rgba(249, 115, 22, 0.4)', 
                                            color: '#F97316', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            🔥 {isRTL() ? `متبقي ${stockCount} قطع فقط!` : `Only ${stockCount} left!`}
                                        </span>
                                    );
                                } else if (stockCount <= 50) {
                                    return (
                                        <span className="trust-badge" style={{ 
                                            background: 'rgba(224, 36, 36, 0.1)', 
                                            border: '1px solid rgba(224, 36, 36, 0.35)', 
                                            color: '#e02424', 
                                            fontWeight: '700',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            ✦ {isRTL() ? `كميات محدودة: متبقي ${stockCount} فقط` : `Limited Quantities: ${stockCount} left!`}
                                        </span>
                                    );
                                } else {
                                    return (
                                        <span className="trust-badge" style={{ 
                                            background: 'rgba(142, 155, 174, 0.1)', 
                                            border: '1px solid rgba(142, 155, 174, 0.2)', 
                                            color: 'var(--text-muted, #8E9BAE)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            ✦ {isRTL() ? `متبقي ${stockCount} قطعة` : `Remaining: ${stockCount}`}
                                        </span>
                                    );
                                }
                            })()}
                            {wholesaleTiers.length > 0 && (
                                <span className="trust-badge" style={{ 
                                    background: 'rgba(16, 185, 129, 0.12)', 
                                    border: '1px solid rgba(16, 185, 129, 0.4)', 
                                    color: '#10B981', 
                                    fontWeight: '700',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    {t('WHOLESALE_AVAILABLE')}
                                </span>
                            )}
                            <span className="trust-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '999px', background: 'var(--bg-highlight)', border: '1px solid var(--border)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text, #111111)' }}>{isRTL() ? `التوصيل المتوقع خلال ${deliveryRange}` : `Estimated delivery ${deliveryRange}`}</span>
                            {product.videoUrl ? <span className="trust-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '999px', background: 'var(--bg-highlight)', border: '1px solid var(--border)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text, #111111)' }}>{t('PRODUCT_VIDEO_INCLUDED')}</span> : null}
                        </div>

                        {/* 6. Variant Selectors (Size, Color, Attributes — placed above fold matching reference screenshot) */}
                        {product.attributes && product.attributes.filter(attr => attr && !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name)).length > 0 && (
                            <div className="product-variants" style={{ margin: '14px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {product.attributes.filter(attr => attr && !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name)).map((attr, idx, filteredAttrs) => {
                                    const isUnlocked = idx === 0 || filteredAttrs.slice(0, idx).every(prevAttr => selectedAttributes[prevAttr.name]);
                                    const prevAttrName = idx > 0 ? filteredAttrs[idx - 1].name : '';
                                    return (
                                        <div 
                                            key={attr.name} 
                                            className="variant-group"
                                            style={{ 
                                                opacity: isUnlocked ? 1 : 0.45, 
                                                pointerEvents: isUnlocked ? 'auto' : 'none', 
                                                transition: 'all 0.3s ease',
                                                cursor: isUnlocked ? 'default' : 'not-allowed'
                                            }}
                                        >
                                            <h4 style={{ 
                                                margin: '0 0 8px 0', 
                                                fontSize: '13.5px', 
                                                fontWeight: 700,
                                                color: validationErrors.includes(attr.name) ? 'var(--red)' : 'inherit'
                                            }}>
                                                {attr.name}
                                                {validationErrors.includes(attr.name) && (
                                                    <span style={{ fontSize: '0.8em', textTransform: 'none', color: 'var(--red)', marginLeft: '8px' }}>
                                                        ({t('PLEASE_SELECT', 'الرجاء الاختيار')})
                                                    </span>
                                                )}
                                                {!isUnlocked && (
                                                    <span style={{ fontSize: '0.85em', textTransform: 'none', color: 'var(--lux-accent, #FE2C55)', marginLeft: '10px' }}>
                                                        ({t('SELECT_PREVIOUS_FIRST', 'الرجاء تحديد') || 'Select'} {prevAttrName} {t('FIRST', 'أولاً') || 'first'})
                                                    </span>
                                                )}
                                            </h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                                                {(Array.isArray(attr.detailedValues) 
                                                    ? attr.detailedValues 
                                                    : (typeof attr.values === 'string' 
                                                        ? attr.values.split(',').map(v => ({ value: v.trim() })) 
                                                        : [])
                                                ).map(val => {
                                                    const valueStr = val.value || val;
                                                    const isSelected = selectedAttributes[attr.name] === valueStr;
                                                    const isColor = val.hexCode || attr.name.toLowerCase().includes('color') || attr.name.includes('اللون');
                                                    return (
                                                        <button
                                                            key={valueStr}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedAttributes(p => ({ ...p, [attr.name]: valueStr }));
                                                                if (validationErrors.includes(attr.name)) {
                                                                    setValidationErrors(p => p.filter(e => e !== attr.name));
                                                                }
                                                            }}
                                                            className={`lux-attr-chip ${isSelected ? 'selected' : ''}`}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '48px',
                                                                gap: '8px',
                                                                padding: isColor && !valueStr ? '4px' : '8px 18px',
                                                                borderRadius: '999px',
                                                                border: isSelected ? '2px solid #111111' : '1px solid #d1d5db',
                                                                background: isSelected ? 'rgba(17, 17, 17, 0.06)' : '#ffffff',
                                                                color: '#111111',
                                                                cursor: isUnlocked ? 'pointer' : 'not-allowed',
                                                                transition: 'all 0.2s ease',
                                                                fontWeight: isSelected ? '800' : '600',
                                                                fontSize: '13.5px'
                                                            }}
                                                            disabled={!isUnlocked}
                                                        >
                                                            {val.imageUrl && (
                                                                <img src={resolveImageUrl(val.imageUrl)} alt={valueStr} style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover' }} />
                                                            )}
                                                            {val.hexCode && !val.imageUrl && (
                                                                <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: val.hexCode, border: '1px solid rgba(0,0,0,0.2)', display: 'inline-block' }} />
                                                            )}
                                                            <span>{valueStr}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* 7. Quantity Selector (Qty [ - | 1 | + ]) & Wholesale Tiers */}
                        <div style={{ margin: '14px 0 18px 0', padding: '12px 14px', background: 'var(--bg-highlight)', border: '1px solid var(--border)', borderRadius: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: wholesaleTiers.length > 0 ? '14px' : '0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text)' }}>
                                        {wholesaleTiers.length > 0 ? (isRTL() ? '💎 كمية الطلب (شرائح الجملة)' : '💎 Qty (Wholesale Tiers)') : (isRTL() ? 'الكمية (Qty)' : 'Qty')}
                                    </span>
                                    {wholesaleTiers.length > 0 && (
                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                            ({isRTL() ? `الحد الأدنى: ${wholesaleTiers[0]?.minQty || 1}` : `Min: ${wholesaleTiers[0]?.minQty || 1}`})
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                                    <button 
                                        type="button"
                                        aria-label="Decrease quantity"
                                        onClick={() => setQuantity(q => Math.max((wholesaleTiers.length > 0 ? Number(wholesaleTiers[0].minQty || 1) : 1), q - 1))}
                                        style={{ width: '36px', height: '34px', border: 'none', background: '#f9fafb', color: '#111111', fontSize: '17px', fontWeight: 'bold', cursor: 'pointer' }}
                                    >−</button>
                                    <input 
                                        type="number"
                                        min={wholesaleTiers.length > 0 ? Number(wholesaleTiers[0].minQty || 1) : 1}
                                        value={quantity}
                                        onChange={e => {
                                            const val = parseInt(e.target.value, 10);
                                            const min = wholesaleTiers.length > 0 ? Number(wholesaleTiers[0].minQty || 1) : 1;
                                            setQuantity(isNaN(val) ? min : Math.max(min, val));
                                        }}
                                        style={{ width: '50px', height: '34px', border: 'none', background: '#ffffff', color: '#111111', textAlign: 'center', fontWeight: '700', fontSize: '14px' }}
                                    />
                                    <button 
                                        type="button"
                                        aria-label="Increase quantity"
                                        onClick={() => setQuantity(q => q + 1)}
                                        style={{ width: '36px', height: '34px', border: 'none', background: '#f9fafb', color: '#111111', fontSize: '17px', fontWeight: 'bold', cursor: 'pointer' }}
                                    >+</button>
                                </div>
                            </div>

                            {wholesaleTiers.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${Math.min(140, 450 / Math.max(1, wholesaleTiers.length))}px, 1fr))`, gap: '8px' }}>
                                    {wholesaleTiers.map((tier, idx) => {
                                        const min = Number(tier.minQty) || 1;
                                        const max = tier.maxQty && Number(tier.maxQty) > 0 ? Number(tier.maxQty) : Infinity;
                                        const isActive = quantity >= min && quantity <= max;
                                        return (
                                            <div 
                                                key={idx}
                                                onClick={() => setQuantity(min)}
                                                style={{ 
                                                    padding: '10px', 
                                                    borderRadius: '10px', 
                                                    background: isActive ? 'rgba(254, 44, 85, 0.05)' : 'var(--bg-base)', 
                                                    border: isActive ? '2px solid #FE2C55' : '1px solid var(--border)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    textAlign: 'center',
                                                    position: 'relative'
                                                }}
                                            >
                                                {isActive && (
                                                    <span style={{ position: 'absolute', top: '-8px', right: '8px', background: '#FE2C55', color: '#ffffff', fontSize: '9px', fontWeight: 800, padding: '2px 6px', borderRadius: '4px' }}>
                                                        {isRTL() ? 'نشط' : 'ACTIVE'}
                                                    </span>
                                                )}
                                                <div style={{ fontSize: '11px', color: isActive ? '#FE2C55' : 'var(--text-muted)', fontWeight: 600 }}>
                                                    {min}{max !== Infinity ? `–${max}` : '+'} {isRTL() ? 'قطعة' : 'pcs'}
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: 800, color: isActive ? '#FE2C55' : 'var(--text)', marginTop: '4px' }}>
                                                    ${Number(tier.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                                    / {isRTL() ? 'قطعة' : 'item'}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        
                        <p className="product-detail-desc">{displayDesc || t('PRODUCT_DESC_FALLBACK')}</p>

                        {/* Dynamic Specifications Extension Panels */}
                        {product.productType === 'PERFUME' && product.typeMetadata && (
                            <div className="lux-pyramid-container">
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', fontWeight: '800', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#111111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'var(--lux-gold)' }}>✦</span> {t('SCENT_SIGNATURE')}
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {product.typeMetadata.scentFamilies && (
                                        <div style={{ fontSize: '13px', color: '#666666', marginBottom: '8px' }}>
                                            <strong>{t('SCENT_FAMILY')}</strong> {product.typeMetadata.scentFamilies}
                                            {product.typeMetadata.concentration && (
                                                <span style={{ marginLeft: '12px', background: 'rgba(184, 145, 84, 0.1)', color: 'var(--lux-gold)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>
                                                    {product.typeMetadata.concentration}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {product.typeMetadata.fragranceNotes?.top && (
                                        <div className="lux-pyramid-level">
                                            <div className="lux-pyramid-title">{t('TOP_NOTES')}</div>
                                            <div className="lux-pyramid-notes">{product.typeMetadata.fragranceNotes.top.join(', ')}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.fragranceNotes?.middle && (
                                        <div className="lux-pyramid-level">
                                            <div className="lux-pyramid-title">{t('HEART_NOTES')}</div>
                                            <div className="lux-pyramid-notes">{product.typeMetadata.fragranceNotes.middle.join(', ')}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.fragranceNotes?.base && (
                                        <div className="lux-pyramid-level">
                                            <div className="lux-pyramid-title">{t('BASE_NOTES')}</div>
                                            <div className="lux-pyramid-notes">{product.typeMetadata.fragranceNotes.base.join(', ')}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.ingredients && (
                                        <div style={{ fontSize: '11.5px', color: '#888888', marginTop: '6px', fontStyle: 'italic', borderTop: '1px dashed #EAEAEA', paddingTop: '10px' }}>
                                            <strong>{t('INGREDIENTS')}</strong> {product.typeMetadata.ingredients}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {product.productType === 'CAR' && product.typeMetadata && (
                            <div style={{ margin: '20px 0' }}>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', fontWeight: '800', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#111111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'var(--lux-gold)' }}>✦</span> {t('VEHICLE_SPECS')}
                                </h3>
                                <div className="lux-car-specs-grid">
                                    {product.typeMetadata.model && (
                                        <div className="lux-car-spec-card">
                                            <span className="lux-car-spec-label">{t('MODEL')}</span>
                                            <span className="lux-car-spec-value">{product.typeMetadata.model}</span>
                                        </div>
                                    )}
                                    {product.typeMetadata.year && (
                                        <div className="lux-car-spec-card">
                                            <span className="lux-car-spec-label">{t('YEAR')}</span>
                                            <span className="lux-car-spec-value">{product.typeMetadata.year}</span>
                                        </div>
                                    )}
                                    {product.typeMetadata.mileage && (
                                        <div className="lux-car-spec-card">
                                            <span className="lux-car-spec-label">{t('MILEAGE')}</span>
                                            <span className="lux-car-spec-value">{product.typeMetadata.mileage.toLocaleString()} km</span>
                                        </div>
                                    )}
                                    {product.typeMetadata.engineSize && (
                                        <div className="lux-car-spec-card">
                                            <span className="lux-car-spec-label">{t('ENGINE')}</span>
                                            <span className="lux-car-spec-value">{product.typeMetadata.engineSize}</span>
                                        </div>
                                    )}
                                    {product.typeMetadata.transmission && (
                                        <div className="lux-car-spec-card" style={{ gridColumn: 'span 2' }}>
                                            <span className="lux-car-spec-label">{t('TRANSMISSION')}</span>
                                            <span className="lux-car-spec-value">{product.typeMetadata.transmission}</span>
                                        </div>
                                    )}
                                    {product.typeMetadata.dealerLocation && (
                                        <div className="lux-car-spec-card" style={{ gridColumn: 'span 2', background: 'rgba(184, 145, 84, 0.04)', borderColor: 'rgba(184, 145, 84, 0.15)' }}>
                                            <span className="lux-car-spec-label" style={{ color: 'var(--lux-gold)' }}>{t('SHOWROOM_LOCATION')}</span>
                                            <span className="lux-car-spec-value" style={{ fontSize: '13px' }}>{product.typeMetadata.dealerLocation}</span>
                                        </div>
                                    )}
                                </div>

                                {product.typeMetadata.engineSoundUrl && (
                                    <div style={{ background: '#F8F8F8', border: '1px solid #EAEAEA', borderRadius: '12px', padding: '12px 16px', margin: '15px 0', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <span style={{ fontSize: '1.2em' }}>🔊</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '11px', color: '#666666', textTransform: 'uppercase', fontWeight: '700' }}>{isRTL() ? 'استمع لزئير المحرك الرياضي' : 'Listen to Engine Sound'}</div>
                                            <audio controls src={product.typeMetadata.engineSoundUrl} style={{ width: '100%', height: '32px', marginTop: '6px' }} />
                                        </div>
                                    </div>
                                )}

                                <div className="lux-car-calculator">
                                    <div className="lux-car-calc-title">✦ {isRTL() ? 'حاسبة التمويل واقساط المرابحة الفاخرة' : 'Luxury VIP Financing Calculator'}</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{isRTL() ? 'سعر السيارة النقدي:' : 'Cash Price:'}</span>
                                            <span style={{ fontWeight: '600' }}>${displayPrice.toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{isRTL() ? 'الدفعة الأولى المقترحة (20%):' : 'Down Payment (20%):'}</span>
                                            <span style={{ fontWeight: '600' }}>${(displayPrice * 0.2).toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{isRTL() ? 'النسبة السنوية APR:' : 'Financing APR:'}</span>
                                            <span style={{ fontWeight: '600', color: 'var(--lux-gold)' }}>{product.typeMetadata.financingApr || 3.49}%</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #222222', paddingTop: '10px', marginTop: '4px', fontSize: '15px' }}>
                                            <span style={{ fontWeight: '700', color: 'var(--lux-gold)' }}>{isRTL() ? 'القسط الشهري المقدر (60 شهر):' : 'Est. Monthly (60 months):'}</span>
                                            <span style={{ fontWeight: '800', color: 'var(--lux-gold)' }}>
                                                ${Math.round(((displayPrice * 0.8) * ((product.typeMetadata.financingApr || 2.99) / 100 / 12) * Math.pow(1 + ((product.typeMetadata.financingApr || 2.99) / 100 / 12), 60)) / (Math.pow(1 + ((product.typeMetadata.financingApr || 2.99) / 100 / 12), 60) - 1)).toLocaleString()} / mo
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {product.productType === 'ELECTRONICS' && product.typeMetadata && (
                            <div style={{ margin: '20px 0' }}>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', fontWeight: '800', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#111111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'var(--lux-gold)' }}>✦</span> {isRTL() ? 'المواصفات التقنية الكاملة' : 'Technical Specifications'}
                                </h3>
                                <div className="lux-tech-specs">
                                    {product.typeMetadata.chipset && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'المعالج' : 'Processor'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.chipset}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.ram && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'الذاكرة العشوائية' : 'RAM'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.ram}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.storage && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'السعة والتخزين' : 'Storage'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.storage}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.batteryCapacity && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'البطارية والتشغيل' : 'Battery Life'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.batteryCapacity}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.warrantyMonths && (
                                        <div className="lux-tech-spec-row" style={{ background: 'rgba(184, 145, 84, 0.04)' }}>
                                            <div className="lux-tech-spec-label" style={{ color: 'var(--lux-gold)' }}>{isRTL() ? 'مدة الضمان الحصري' : 'Elite Warranty'}</div>
                                            <div className="lux-tech-spec-value" style={{ fontWeight: '700', color: 'var(--lux-gold)' }}>{product.typeMetadata.warrantyMonths} {isRTL() ? 'أشهر (ضمان محلي شامل)' : 'Months Premium Warranty'}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {product.productType === 'FASHION' && product.typeMetadata && (
                            <div style={{ margin: '20px 0' }}>
                                <div style={{ background: '#F8F8F8', border: '1px solid #EAEAEA', borderRadius: '12px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {product.typeMetadata.fabric && (
                                        <div style={{ fontSize: '13px', color: '#222222' }}>
                                            <strong>{isRTL() ? 'تركيبة النسيج والخامة:' : 'Fabric & Material:'}</strong> {product.typeMetadata.fabric}
                                        </div>
                                    )}
                                    {product.typeMetadata.gender && (
                                        <div style={{ fontSize: '13.5px', color: '#666666' }}>
                                            <strong>{isRTL() ? 'الفئة المستهدفة:' : 'Gender Focus:'}</strong> {product.typeMetadata.gender}
                                        </div>
                                    )}
                                    {product.typeMetadata.sizeChartUrl && (
                                        <div style={{ borderTop: '1px dashed #EAEAEA', paddingTop: '10px', marginTop: '6px' }}>
                                            <a 
                                                href={product.typeMetadata.sizeChartUrl} 
                                                target="_blank" 
                                                rel="noreferrer" 
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: 'var(--lux-gold)', textDecoration: 'none' }}
                                            >
                                                📏 {isRTL() ? 'عرض جدول المقاسات الرسمي المعتمد (PDF)' : 'View Certified Sizing Guide (PDF) ✦'}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {product.productType === 'WATCH' && product.typeMetadata && (
                            <div style={{ margin: '20px 0' }}>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', fontWeight: '800', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#111111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'var(--lux-gold)' }}>✦</span> {isRTL() ? 'المواصفات الفنية للساعة الفاخرة' : 'Luxury Timepiece Specifications'}
                                </h3>
                                <div className="lux-tech-specs">
                                    {product.typeMetadata.movement && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'حركة الساعة' : 'Movement'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.movement}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.caliber && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'العيار/الحركة الدقيقة' : 'Caliber'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.caliber}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.caseMaterial && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'مادة الهيكل' : 'Case Material'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.caseMaterial}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.dialColor && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'لون المينا' : 'Dial Color'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.dialColor}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.caseDiameter && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'قطر الهيكل' : 'Case Diameter'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.caseDiameter}</div>
                                        </div>
                                    )}
                                    {product.typeMetadata.waterResistance && (
                                        <div className="lux-tech-spec-row">
                                            <div className="lux-tech-spec-label">{isRTL() ? 'مقاومة الماء' : 'Water Resistance'}</div>
                                            <div className="lux-tech-spec-value">{product.typeMetadata.waterResistance}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Fallback Basic Specs Block (Alibaba style) - dynamically populated from database attributes */}
                        {product.productType !== 'PERFUME' && product.productType !== 'CAR' && product.productType !== 'ELECTRONICS' && product.productType !== 'FASHION' && product.productType !== 'WATCH' && product.attributes && product.attributes.filter(attr => attr && !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name)).length > 0 && (
                            <div style={{ margin: '25px 0', background: 'var(--bg-highlight, #F7F7F7)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border, #E5E5E5)' }}>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', fontWeight: '800', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#111111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'var(--lux-accent, #FE2C55)' }}>✦</span> {isRTL() ? 'المواصفات الأساسية' : 'Key Specifications'}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                    {product.attributes.filter(attr => attr && !['WHOLESALE_TIERS', 'INVENTORY_RULES'].includes(attr.name)).map(attr => {
                                        const valueStr = selectedAttributes[attr.name] || (Array.isArray(attr.detailedValues) ? attr.detailedValues[0]?.value : (typeof attr.values === 'string' ? attr.values.split(',')[0]?.trim() : ''));
                                        return (
                                            <div key={attr.name} style={{ padding: '8px 12px', background: '#ffffff', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.03)' }}>
                                                <div style={{ fontSize: '11px', color: '#888888', fontWeight: 600 }}>{attr.name}</div>
                                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#111111', marginTop: '2px' }}>{valueStr || '-'}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="product-detail-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '20px 0' }}>
                            <button 
                                className={`btn-primary btn-lg ${validationErrors.length > 0 ? 'shake-animation' : ''}`} 
                                style={{ flex: 1, minWidth: '150px', background: '#111827', color: '#ffffff', border: '1px solid #111827', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', borderRadius: '12px' }}
                                onClick={handleAddToCart} 
                                disabled={(inventoryRules?.trackInventory !== false && product?.stock < 99999) && selectedVariant && selectedVariant.quantity <= 0}
                            >
                                <ShoppingBag size={18} /> {(inventoryRules?.trackInventory !== false && product?.stock < 99999) && selectedVariant && selectedVariant.quantity <= 0 ? 'OUT OF STOCK' : t('ADD_TO_CART_BTN')}
                            </button>
                            <button 
                                type="button"
                                className="btn-primary btn-lg"
                                style={{ flex: 1, minWidth: '150px', background: '#e02424', color: '#ffffff', border: '1px solid #e02424', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', borderRadius: '12px', boxShadow: '0 4px 14px rgba(224, 36, 36, 0.25)' }}
                                onClick={handleBuyNow}
                            >
                                <span>⚡ {isRTL() ? 'شراء الآن' : 'Buy Now'}</span>
                            </button>
                            <button 
                                className="btn-icon" 
                                onClick={() => setIsWishlisted(!isWishlisted)}
                                style={{ color: isWishlisted ? '#e02424' : '#111827', transition: 'all 0.2s ease', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#ffffff' }}
                            >
                                <Heart size={18} fill={isWishlisted ? '#e02424' : 'none'} color={isWishlisted ? '#e02424' : '#111827'} />
                            </button>
                            <button className="btn-icon" style={{ borderRadius: '12px', border: '1px solid #e5e7eb', background: '#ffffff' }}><Share2 size={18} color="#111827" /></button>
                        </div>

                        {/* Ask Seller & Verified Merchant Bar */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255, 255, 255,0.08)', border: '1px solid rgba(255, 255, 255,0.25)', borderRadius: '12px', margin: '14px 0', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#111827', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' }}>
                                    🏛️
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Link to={`/seller/${product?.vendorId || 'verified-merchant'}`} style={{ color: '#111827', fontWeight: '700', fontSize: '13px', textDecoration: 'none' }}>
                                            {product?.vendorName || (isRTL() ? 'تاجر معتمد لدى LUXVONO' : 'LUXVONO Verified Partner')}
                                        </Link>
                                        <span style={{ background: '#10B981', color: '#fff', fontSize: '9px', fontWeight: '800', padding: '1px 6px', borderRadius: '4px' }}>VERIFIED</span>
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                        {isRTL() ? '⚡ رد سريع خلال أقل من ساعة' : '⚡ Fast Response within 1 hour'}
                                    </span>
                                </div>
                            </div>

                            <button 
                                type="button"
                                onClick={handleAskSeller}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#111827',
                                    color: '#FFFFFF',
                                    border: '1px solid #FFFFFF',
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}
                            >
                                <MessageCircle size={15} />
                                <span>{isRTL() ? 'تواصل مع البائع' : 'Ask Seller'}</span>
                            </button>
                        </div>

                        {/* Secured Payment and Trust Logos (Alibaba style) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '20px 0', padding: '16px', background: 'var(--bg-highlight, #F7F7F7)', borderRadius: '16px', border: '1px solid var(--border, #E5E5E5)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: '#10B981', fontSize: '15px' }}>🛡️</span>
                                <strong style={{ fontSize: '13px', color: '#111827' }}>{isRTL() ? 'حماية المشتري و Trade Assurance' : 'Trade Assurance & Buyer Protection'}</strong>
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.4 }}>
                                {isRTL() ? 'دفع آمن ومحمي بنسبة 100% مع ضمان استرداد الأموال والشحن السريع الدولي.' : '100% secure payment with complete order protection, money-back guarantee, and global shipping.'}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                                <span style={{ background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E5E5', fontSize: '10px', fontWeight: '800', color: '#0A3A8E', fontFamily: 'sans-serif' }}>VISA</span>
                                <span style={{ background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E5E5', fontSize: '10px', fontWeight: '800', color: '#002C9B', fontFamily: 'sans-serif' }}>PayPal</span>
                                <span style={{ background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E5E5', fontSize: '10px', fontWeight: '800', color: '#000000', fontFamily: 'sans-serif' }}> Pay</span>
                                <span style={{ background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E5E5', fontSize: '10px', fontWeight: '800', color: '#eb001b', fontFamily: 'sans-serif' }}>Mastercard</span>
                                <span style={{ background: '#ffffff', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E5E5', fontSize: '10px', fontWeight: '800', color: '#111111', fontFamily: 'sans-serif' }}>Afterpay</span>
                            </div>
                        </div>
                    </div>
                </div>

                {frequentlyBoughtTogether.length > 0 && (
                    <section className="section">
                        <div className="section-header">
                            <div>
                                <div className="section-kicker">Bundle up</div>
                                <h2 className="section-title">Frequently bought together</h2>
                                <p className="section-subtitle">Customers who bought this also loved these items</p>
                                <div className="section-accent" />
                            </div>
                        </div>
                        <div className="product-grid" style={{ paddingTop: '10px' }}>
                            {frequentlyBoughtTogether.map(p => <ProductCard key={`bundle-${p.id}`} product={p} onAddToCart={onAddToCart} onBuyNow={onBuyNow} />)}
                        </div>
                    </section>
                )}

                {completeTheLook.length > 0 && (
                    <section className="section">
                        <div className="section-header">
                            <div>
                                <div className="section-kicker">Style pairing</div>
                                <h2 className="section-title">Complete the look</h2>
                                <p className="section-subtitle">Pair it with these to complete your style</p>
                                <div className="section-accent" />
                            </div>
                        </div>
                        <div className="product-grid" style={{ paddingTop: '10px' }}>
                            {completeTheLook.map(p => <ProductCard key={`look-${p.id}`} product={p} onAddToCart={onAddToCart} onBuyNow={onBuyNow} />)}
                        </div>
                    </section>
                )}

                {related.length > 0 && (
                    <section className="section">
                        <div className="section-header">
                            <div>
                                <div className="section-kicker">{isRTL() ? 'منتجات ذات صلة' : 'Related products'}</div>
                                <h2 className="section-title">{t('YOU_MAY_ALSO_LIKE')}</h2>
                                <p className="section-subtitle">{isRTL() ? 'المزيد من المنتجات التي قد تنال إعجابك' : 'More products you might love'}</p>
                                <div className="section-accent" />
                            </div>
                        </div>
                        <div className="product-grid" style={{ paddingTop: '10px' }}>
                            {related.map(p => <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} onBuyNow={onBuyNow} />)}
                        </div>
                    </section>
                )}

                {recentlyViewedList.length > 0 && (
                    <section className="section">
                        <div className="section-header">
                            <div>
                                <div className="section-kicker">{isRTL() ? 'تصفحته مؤخراً' : 'Recently viewed'}</div>
                                <h2 className="section-title">{isRTL() ? 'منتجات شاهدتها سابقاً' : 'Pick up where you left off'}</h2>
                                <div className="section-accent" />
                            </div>
                        </div>
                        <div className="product-grid" style={{ paddingTop: '10px' }}>
                            {recentlyViewedList.map(p => <ProductCard key={`recently-${p.id}`} product={p} onAddToCart={onAddToCart} onBuyNow={onBuyNow} />)}
                        </div>
                    </section>
                )}

                {/* Slot: bottom */}
                <DynamicSectionsRenderer
                    page="product-page"
                    position="bottom"
                    allProducts={allProducts}
                    allCategories={categories}
                    onAddToCart={onAddToCart}
                    storeSettings={storeSettings}
                />

                {/* Slot: before-footer */}
                <DynamicSectionsRenderer
                    page="product-page"
                    position="before-footer"
                    allProducts={allProducts}
                    allCategories={categories}
                    onAddToCart={onAddToCart}
                    storeSettings={storeSettings}
                />
            </div>

            {/* Draggable Floating Mini-Video Player (when product has an associated video) */}
            {primaryVideoUrl && (
                <FloatingMiniVideoPlayer
                    videoUrl={primaryVideoUrl}
                    posterUrl={resolveImageUrl(product.primaryImageUrl || product.image || mediaList[0])}
                    productName={displayName}
                    isRTL={isRTL()}
                    initiallyMuted={true}
                />
            )}

            {/* Full-Screen High-Resolution Image Lightbox Modal */}
            {isLightboxOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setIsLightboxOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 100040,
                        background: 'rgba(0, 0, 0, 0.94)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 'env(safe-area-inset-top, 16px) 16px env(safe-area-inset-bottom, 20px)'
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '16px',
                            left: '16px',
                            right: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            zIndex: 10
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 700, background: 'rgba(255,255,255,0.14)', padding: '5px 12px', borderRadius: '999px' }}>
                            {activeMediaIndex + 1} / {mediaList.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsLightboxOpen(false)}
                            aria-label="Close full-screen viewer"
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.16)',
                                color: '#ffffff',
                                border: '1px solid rgba(255,255,255,0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsZoomed(z => !z);
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: '920px',
                            height: '72vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: isZoomed ? 'zoom-out' : 'zoom-in'
                        }}
                    >
                        <img
                            src={resolveImageUrl(activeMedia)}
                            alt={displayName}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: isZoomed ? 'scale(1.85)' : 'scale(1)',
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    </div>

                    {mediaList.length > 1 && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                display: 'flex',
                                gap: '8px',
                                overflowX: 'auto',
                                maxWidth: '100%',
                                padding: '12px 4px 4px'
                            }}
                        >
                            {mediaList.map((m, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                        setIsZoomed(false);
                                        goToMedia(idx);
                                    }}
                                    style={{
                                        width: '54px',
                                        height: '54px',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        border: idx === activeMediaIndex ? '2px solid #FE2C55' : '1.5px solid rgba(255,255,255,0.25)',
                                        background: '#111827',
                                        padding: 0,
                                        flexShrink: 0,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {m.endsWith('.mp4') || m.endsWith('.m3u8') ? (
                                        <span style={{ color: '#fff', fontSize: '14px' }}>▶</span>
                                    ) : (
                                        <img src={resolveImageUrl(m)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Toast feedback for Share / Cart */}
            {(cartAddedToast || shareCopiedToast) && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '84px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 100030,
                        background: 'rgba(17, 24, 39, 0.94)',
                        color: '#ffffff',
                        padding: '10px 18px',
                        borderRadius: '999px',
                        fontSize: '13px',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.28)'
                    }}
                >
                    <Check size={16} color="#10B981" />
                    <span>
                        {shareCopiedToast
                            ? (isRTL() ? 'تم نسخ رابط المنتج' : 'Product link copied!')
                            : (isRTL() ? 'تمت الإضافة إلى السلة بنجاح' : 'Added to cart!')}
                    </span>
                </div>
            )}

            {/* Mobile Sticky Action Bar (matches reference screenshot with outlined Add to Cart & solid Buy Now pills) */}
            <div className="mobile-sticky-buy-bar">
                <button 
                    className="btn-sticky-cart" 
                    type="button"
                    onClick={handleAddToCart}
                    disabled={isOutOfStock || isSubmittingCart}
                    style={{
                        flex: 1,
                        height: '48px',
                        borderRadius: '999px',
                        background: '#ffffff',
                        color: '#111111',
                        border: '1.5px solid #111111',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.15,
                        padding: '4px 12px',
                        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                        opacity: isOutOfStock ? 0.5 : 1
                    }}
                >
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#111111' }}>
                        {isOutOfStock
                            ? (isRTL() ? 'نفد المخزون' : 'Out of stock')
                            : (cartAddedToast ? (isRTL() ? '✓ تمت الإضافة' : '✓ Added') : t('ADD_TO_CART_BTN'))}
                    </span>
                    {discountPercent && !isOutOfStock ? (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#374151' }}>
                            {discountPercent}% OFF
                        </span>
                    ) : null}
                </button>
                <button 
                    className="btn-sticky-buy" 
                    type="button"
                    onClick={handleBuyNow}
                    disabled={isOutOfStock || isSubmittingBuy}
                    style={{
                        flex: 1.15,
                        height: '48px',
                        borderRadius: '999px',
                        background: 'linear-gradient(90deg, #FE2C55 0%, #e02424 100%)',
                        color: '#ffffff',
                        border: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.15,
                        padding: '4px 12px',
                        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                        opacity: isOutOfStock ? 0.5 : 1,
                        boxShadow: '0 4px 14px rgba(224, 36, 36, 0.28)'
                    }}
                >
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
                        ⚡ {isRTL() ? 'شراء الآن' : 'Buy now'}
                    </span>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.92)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                        {isRTL() ? `توصيل سريع ${deliveryRange}` : `Fastest delivery ${deliveryRange}`}
                    </span>
                </button>
            </div>

        </div>
    );
};

export default ProductPage;
